<template>
  <AdminLayout @logout="logout">
    <div class="iframe-page-wrapper">
      <!-- Account Selection Screen -->
      <div v-if="!selectedAccountId && !hasAccessDenied" class="account-selection-container">
        <div class="w-100">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Select Account</h2>
            <p class="text-gray-600">Please select an account to continue.</p>
          </div>

          <!-- Search Bar -->
          <div class="mb-6">
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search accounts..."
                class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          
          <div v-if="loadingAccounts" class="text-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p class="text-gray-600 mt-4">Loading accounts...</p>
          </div>
          
          <div v-else-if="filteredAccounts.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div
              v-for="account in filteredAccounts"
              :key="account.id"
              @click="selectAccount(account)"
              class="bg-white p-6 border border-gray-200 rounded-lg hover:border-red-500 hover:shadow-md cursor-pointer transition-all"
            >
              <div class="flex flex-col items-center text-center">
                <img
                  v-if="account.avatar"
                  :src="account.avatar"
                  class="w-16 h-16 rounded-full object-cover mb-4"
                  alt="Account avatar"
                />
                <div v-else class="w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center text-white font-bold text-xl mb-4">
                  {{ account.organization ? account.organization.charAt(0).toUpperCase() : 'A' }}
                </div>
                <h3 class="font-semibold text-gray-900 text-lg mb-1">{{ account.organization || 'Account' }}</h3>
                <p class="text-sm text-gray-500">ID: {{ account.id }}</p>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-gray-500 mt-2">No accounts found.</p>
          </div>
        </div>
      </div>

      <!-- Loading State (checking access) -->
      <div v-if="!hasAccess && !hasAccessDenied && !selectedAccountId" class="text-center w-100 loader-container">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        <p class="text-gray-600 mt-4">Checking access...</p>
      </div>

      <!-- Iframe Content -->
      <iframe 
        v-if="iframeUrl && hasAccess && selectedAccountId" 
        :src="iframeUrl" 
        class="external-iframe"
        @load="onIframeLoad"
      />

      <!-- Access Denied Modal -->
      <div
        v-if="hasAccessDenied"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <!-- Modal panel -->
        <div class="flex min-h-screen items-center justify-center p-4">
          <div
            class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
          >
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <!-- Icon -->
                  <svg
                    class="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3
                    class="text-lg font-medium leading-6 text-gray-900"
                    id="modal-title"
                  >
                    Access Denied
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      {{ accessMessage }} Please select another account with proper access permissions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 sm:gap-3">
              <button
                type="button"
                class="inline-flex w-full justify-center items-center rounded-md bg-red-600 px-6 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto sm:text-sm whitespace-nowrap"
                @click="selectDifferentAccount"
              >
                Select Other
              </button>
              <button
                type="button" style="margin-top: 0 !important;"
                class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-6 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                @click="goBack"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/components/AdminLayout.vue";
import axios from "axios";

export default {
  name: "ExternalIframeView",
  components: {
    AdminLayout,
  },
  props: {
    // The base URL of the external service
    baseUrl: {
      type: String,
      required: true,
    },
    // The account ID or client ID to pass
    accountIdKey: {
      type: String,
      default: "accountId",
    },
    // The access token key to pass
    tokenKey: {
      type: String,
      default: "accessToken",
    },
    // Roles that have access (empty array = all roles have access)
    allowedRoles: {
      type: Array,
      default: () => [],
    },
    // Custom access check function
    customAccessCheck: {
      type: Function,
      default: null,
    },
    // Custom message when access is denied
    deniedMessage: {
      type: String,
      default: "Changing roles requires an admin/manager access.",
    },
  },
  data() {
    return {
      iframeUrl: null,
      hasAccess: false,
      hasAccessDenied: false,
      accessToken: null,
      currentUser: null,
      currentClient: null,
      accessMessage: "",
      uptodateToken: null,
      selectedAccountId: null,
      accounts: [],
      loadingAccounts: false,
      searchQuery: "",
    };
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return (
        this.shopSettings?.apiUrl ||
        "https://selectair-vakanties.uptodatesites.com"
      );
    },
    filteredAccounts() {
      if (!this.searchQuery) {
        return this.accounts;
      }
      const query = this.searchQuery.toLowerCase();
      return this.accounts.filter(account => {
        const org = (account.organization || '').toLowerCase();
        const id = String(account.id || '');
        return org.includes(query) || id.includes(query);
      });
    },
  },
  async created() {
    // Clear any previous account selection on page load/refresh
    this.selectedAccountId = null;
    localStorage.removeItem('currentClient');
    
    // Load accounts for selection
    await this.loadAccounts();
  },
  methods: {
    async checkAccess() {
      try {
        // Reset access states
        this.hasAccess = false;
        this.hasAccessDenied = false;
        
        // Get admin token and user
        const token = localStorage.getItem("admin_token");
        const uptodateToken = localStorage.getItem("uptodate_token");
        const userStr = localStorage.getItem("admin_user");

        if (!token || !userStr) {
          this.hasAccessDenied = true;
          this.accessMessage = "You need to be logged in.";
          return;
        }

        // Verify token is still valid
        const verifyResponse = await axios.get(`${this.apiUrl}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!verifyResponse.data.success) {
          this.hasAccessDenied = true;
          this.accessMessage = "Your session has expired. Please log in again.";
          return;
        }

        this.currentUser = JSON.parse(userStr);
        this.accessToken = token;
        this.uptodateToken = uptodateToken;

        // If custom access check function is provided, use it
        if (this.customAccessCheck) {
          const accessResult = this.customAccessCheck(this.currentUser);
          if (accessResult === true) {
            this.hasAccess = true;
          } else {
            this.hasAccessDenied = true;
            this.accessMessage = typeof accessResult === "string" 
              ? accessResult 
              : this.deniedMessage;
          }
          return;
        }

        // Check access based on role from currentClient
        const currentClientStr = localStorage.getItem('currentClient');
        let currentSelectedBlogger = null;
        
        if (currentClientStr) {
          currentSelectedBlogger = JSON.parse(currentClientStr);
        }

        // Check if user has access based on blogger role
        if (
          (currentSelectedBlogger &&
            currentSelectedBlogger.blogger &&
            currentSelectedBlogger.blogger.length > 0 &&
            [6, 7].indexOf(currentSelectedBlogger.blogger[0].role.roleId) >= 0) ||
          this.currentUser.roleId == 2
        ) {
          this.hasAccess = true;
        } else {
          this.hasAccessDenied = true;
          this.accessMessage = "You don't have permission to access this service with the selected account.";
        }
      } catch (error) {
        console.error("Error checking access:", error);
        this.hasAccessDenied = true;
        this.accessMessage = "An error occurred while checking your access.";
      }
    },
    async loadAccounts() {
      this.loadingAccounts = true;
      try {
        // Get token directly from localStorage
        const token = localStorage.getItem("uptodate_token") || localStorage.getItem("admin_token");
        
        if (!token) {
          console.error("No token available for loading accounts");
          return;
        }
        
        const response = await axios.get('https://www.uptodateconnect.com/api/v1/me/accounts', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          this.accounts = response.data.payload || [];
          console.log("Loaded accounts:", this.accounts);
          // Don't auto-select any account - always show selection screen
        }
      } catch (error) {
        console.error("Error loading accounts:", error);
      } finally {
        this.loadingAccounts = false;
      }
    },
    async selectAccount(account) {
      console.log("Selected account:", account);
      
      // Save to localStorage for access check
      localStorage.setItem("currentClient", JSON.stringify(account));
      
      // Check access with the newly selected account
      await this.checkAccess();
      
      // If access granted, set the account and build URL
      if (this.hasAccess) {
        this.selectedAccountId = account.id;
        this.buildIframeUrl(account.id);
      }
      // If access denied, the modal will show and user can select another account
    },
    buildIframeUrl(accountId) {
      if (!this.hasAccess) return;

      // Build URL with query parameters
      const url = new URL(this.baseUrl);
      
      // Add access token
      url.searchParams.append(this.tokenKey, this.uptodateToken || this.accessToken);
      
      // Add account/client ID
      if (accountId) {
        url.searchParams.append(this.accountIdKey, accountId);
      }

      console.log("Built iframe URL:", url.toString());
      this.iframeUrl = url.toString();
    },
    onIframeLoad() {
      console.log("Iframe loaded successfully");
    },
    logout() {
      // Clear auth data
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      localStorage.removeItem("uptodate_token");
      localStorage.removeItem("currentClient");
      // Redirect to login
      this.$router.push("/admin/login");
    },
    goBack() {
      this.$router.push("/admin/dashboard");
    },
    selectDifferentAccount() {
      // Reset state to allow selecting a different account
      this.hasAccessDenied = false;
      this.selectedAccountId = null;
      this.hasAccess = false;
      this.iframeUrl = null;
      localStorage.removeItem('currentClient');
    },
  },
};
</script>

<style scoped>
.iframe-page-wrapper {
  position: relative;
  width: 100%;
  height: calc(100vh - 64px); /* Adjust based on your header height */
  overflow: hidden;
}

.external-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.loader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 400px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.account-selection-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 2rem 0;
}

/* Mobile responsiveness */
@media screen and (max-width: 767px) {
  .iframe-page-wrapper {
    height: calc(100vh - 56px); /* Adjust for mobile header */
  }
  
  .account-selection-container {
    padding: 1rem 0;
  }
}
</style>

